/**
 * A custom type for ISO date strings (YYYY-MM-DD)
 */
export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export class DateISO {
  private readonly value: string;

  constructor(dateStr: string | null) {
    if (!dateStr) {
      throw new Error("Date string cannot be null");
    }
    if (!ISO_DATE_REGEX.test(dateStr)) {
      throw new Error("Invalid ISO date format. Expected YYYY-MM-DD");
    }
    const date = new Date(dateStr + "T00:00:00");
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }
    this.value = dateStr;
  }

  static fromString(dateStr: string | null): DateISO | null {
    if (!dateStr) return null;
    try {
      return new DateISO(dateStr);
    } catch {
      console.error(
        `Invalid ISO date format. Expected YYYY-MM-DD. Received ${dateStr}`,
      );
      return null;
    }
  }

  toDate(): Date {
    return new Date(this.value + "T00:00:00");
  }

  toString(): string {
    return this.value;
  }

  format(): string {
    return this.toDate().toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  }

  /**
   * Compares this date with another date (either DateISO object or string)
   * @param other The date to compare with (DateISO or string in YYYY-MM-DD format)
   * @returns 0 if equal, negative if this date is before, positive if this date is after
   */
  compareTo(other: DateISO | string): number {
    const otherStr =
      other instanceof DateISO ? other.toString() : String(other);
    // Simple string comparison works for ISO date format (YYYY-MM-DD)
    return this.value.localeCompare(otherStr);
  }

  /**
   * Checks if this date equals another date
   */
  equals(other: DateISO | string): boolean {
    return this.compareTo(other) === 0;
  }

  /**
   * Checks if this date is before another date
   */
  isBefore(other: DateISO | string): boolean {
    return this.compareTo(other) < 0;
  }

  /**
   * Checks if this date is after another date
   */
  isAfter(other: DateISO | string): boolean {
    return this.compareTo(other) > 0;
  }

  /**
   * Implement valueOf to enable direct comparison with operators (>, <, ===)
   * Returns string representation to allow direct string-based comparison
   */
  valueOf(): string {
    return this.value;
  }
}
